/**
 * 粘贴框计数器的 **React 包装**（纯逻辑在 `importCounter.ts`，有单测）。
 *
 * 两件事只在这里做：
 *
 * 1. **`useDeferredValue`**：输入很长时（例如一次粘 2 000 个字），解析放到下一帧，
 *    打字本身不会被它卡住。数字会晚一帧出现 —— 这是可接受的，因为它是"参考值"，
 *    权威口径永远是下面那份预览。
 * 2. **复用预览数字**：`reportedUnits` 有值时（同一份文本已经预览过）不再解析。
 *    ⚠️ 那时用**当前**文本（不能用 deferred 的旧文本配新的数字，两个数会短暂对不上）。
 */
import { useDeferredValue, useMemo } from 'react';
import {
  countImportText,
  type ImportCounter,
  type ImportCounterInput,
} from './importCounter';

export function useImportCounter(input: ImportCounterInput): ImportCounter {
  const reported = input.reportedUnits ?? null;
  const deferredText = useDeferredValue(input.text);
  /** 有预览数字时用当前文本（省一次解析、也不会有"数字配旧文本"的一帧）；否则用 deferred。 */
  const text = reported !== null ? input.text : deferredText;
  const { listType, defaultTextbook, limit } = input;

  return useMemo(
    () =>
      countImportText({
        text,
        listType,
        ...(defaultTextbook !== undefined ? { defaultTextbook } : {}),
        ...(reported !== null ? { reportedUnits: reported } : {}),
        ...(limit !== undefined ? { limit } : {}),
      }),
    [text, listType, defaultTextbook, reported, limit],
  );
}
